







































































import {
  defineComponent,
  useContext,
  onServerPrefetch,
  onMounted,
  watch,
  ref,
  computed
} from '@nuxtjs/composition-api';
import {
  usePLPEssentials,
  useAlgoliaSearch,
  useAlgoliaInsights,
  useTranslations
} from '@vue-storefront/novulo';
import { buildApiParams } from '~/components/molecules/FilterFacet.vue';
import { useVSFContext } from '@vue-storefront/core';
import { getSSRRequestFullUrl } from '@/helpers/utils';
import useUrlFormatter from '~/hooks/useUrlFormatter';
import { useRouteSync } from '~/composables/useRouteSync';
import { useProductListing } from '~/composables/useProductListing';
import { useProductListingBreadcrumbs } from '~/composables/useProductListingBreadcrumbs';
import { useProductListingAnalytics } from '~/composables/useProductListingAnalytics';
import { useProductListingHead } from '~/composables/useProductListingHead';
import { useSortingOptions } from '~/composables/useSortingOptions';
import { useEssentialsData } from '~/composables/useEssentialsData';
import { CacheTagPrefix } from '@vue-storefront/cache';

export default defineComponent({
  name: 'PLPV2',
  meta: { allowClientNavigation: true },
  setup(_, context) {
    const { getPLPEssentials, PLPEssentialsResult } = usePLPEssentials();
    const { sortingOptions } = useSortingOptions();

    const { i18n, error: req, res, $config } = useContext();
    const formatUrl = useUrlFormatter();
    const vsfContext = useVSFContext();
    const { translate, hasTranslation } = useTranslations();

    const {
      searchState,
      products,
      totalCount,
      nbPages,
      banners,
      headerBanners,
      facets,
      facetsStats,
      isLoading,
      search,
      searchQueryID
    } = useAlgoliaSearch(translate);

    const {
      trackProductClick,
      trackAddToCartSRP,
      trackFiltersEvent,
      trackProductsViewed,
      ALGOLIA_PRODUCTS_CLICKED,
      ALGOLIA_PRODUCTS_ADDED,
      addToSessionStorage
    } = useAlgoliaInsights();

    const { introData, seoData } = useEssentialsData(PLPEssentialsResult);

    const {
      pageCurrent,
      pagePrevious,
      pageNext,
      selectedSort,
      isMoreProducts,
      previousPageUrl,
      nextPageUrl,
      currentPageUrl,
      isLoadingPreviousPage,
      isLoadingNextPage,
      updateSelectedSort,
      updateFilters,
      loadNextPage,
      loadPreviousPage
    } = useProductListing({
      searchState,
      sortingOptions,
      totalCount,
      baseUrl: process.server ? getSSRRequestFullUrl(req) : window.location.href
    });

    const { formattedBreadcrumbs } = useProductListingBreadcrumbs({
      store: context.root.$store,
      i18n,
      formatUrl,
      title: '',
      gtmDispatch: (categoryPath) =>
        context.root.$store.dispatch('gtm-init/setExtraParams', {
          category: categoryPath
        })
    });

    useProductListingAnalytics(products, pageCurrent, vsfContext);

    const { pullFromRoute } = useRouteSync({
      searchState,
      selectedSort,
      sortingOptions
    });

    onServerPrefetch(async ({ $vnode }) => {
      const searchParams = $vnode?.ssrContext?.req?._parsedUrl?.search || '';
      const pageId = context.root.$store.getters['page/getPageId'];

      await getPLPEssentials({
        id: pageId,
        params: buildApiParams(searchParams).replace(/&page=[^&]+/, ''),
        isAlgolia: true
      });

      searchState.value.filters = `primaryTag.id:${pageId}`;
      pullFromRoute();

      if (!products.value.length) {
        await search();
      }

      if (process.server && res && products.value?.length) {
        const tags: string[] = [
          `${CacheTagPrefix.View}category`,
          `${CacheTagPrefix.Category}${pageId}`,
          ...products.value.map((p: any) => `${CacheTagPrefix.Product}${p.product_id}`)
        ];

        const MAX_TAGS = 200;
        const safeTags = tags.slice(0, MAX_TAGS);

        res.setHeader('Cache-Tag', safeTags.join(','));
        res.setHeader(
          'Cache-Control',
          `max-age=${$config.cfBrowserCacheTimeout}, public`
        );
        res.setHeader(
          'Cloudflare-CDN-Cache-Control',
          `max-age=${$config.cfCacheTimeout}, public`
        );
      }
    });

    const currentPage = computed(() => searchState.value.page + 1);
    const handlePagination = (page: number) => {
      searchState.value.page = page - 1;
    };

    const activeFilter = ref<Array<{ filter: string; category: string}>>([]);
    const trackFilterChange = (payload) => {
      if (payload.selected) {
        activeFilter.value.push(
          { filter: payload.filter,
            category: payload.category}
        );
      }
    };

    const handleProductClick = ({ product, index, event }) => {
      const payload = {
        objectID: product.sku.toString(),
        position: index + 1 + (searchState.value.page * searchState.value.hitsPerPage),
        ...(searchQueryID.value && { queryID: searchQueryID.value })
      };

      if (event === 'click') {
        trackProductClick(payload);

        addToSessionStorage(ALGOLIA_PRODUCTS_CLICKED, product.sku, searchQueryID.value);
      }

      if (event === 'addtocart') {
        trackAddToCartSRP(payload);

        addToSessionStorage(ALGOLIA_PRODUCTS_ADDED, product.sku, searchQueryID.value);
      }

      if (activeFilter.value.length) {
        const eventName = event === 'click' ? 'viewed' : 'converted';
        trackFiltersEvent(
          eventName,
          {
            filters: activeFilter.value
          }
        );
      }
    };

    onMounted(()=>{
      trackProductsViewed({
        objectIDs: products.value.map(product => product.id.toString())
      });
    });

    watch(
      products,
      (newProducts) => {
        trackProductsViewed({
          objectIDs: newProducts.map(product => product.id.toString())
        });
      },
      { deep: true }
    );

    return {
      PLPEssentialsResult,
      banners,
      headerBanners,
      currentPage,
      currentPageUrl,
      facets,
      facetsStats,
      formattedBreadcrumbs,
      isLoading,
      isLoadingNextPage,
      isLoadingPreviousPage,
      isMoreProducts,
      introData,
      nbPages,
      nextPageUrl,
      pageCurrent,
      pageNext,
      pagePrevious,
      previousPageUrl,
      products,
      selectedSort,
      seoData,
      sortingOptions,
      totalCount,
      searchState,
      handlePagination,
      handleProductClick,
      hasTranslation,
      loadNextPage,
      loadPreviousPage,
      trackFilterChange,
      translate,
      updateFilters,
      updateSelectedSort
    };
  },
  head() {
    return useProductListingHead(
      this.PLPEssentialsResult,
      this.formattedBreadcrumbs,
      this.$ssrContext,
      this.$i18n,
      this.translate,
      this.hasTranslation,
      this.$store.getters['page/getUrlInfo']
    );
  }
});
